import { API_PATHS } from '@/core/api/api-paths'
import { env } from '@/core/config/env'
import {
  clearSession,
  isSessionExpiringSoon,
  loadSession,
  type Session,
  saveSession,
} from './session'

interface RefreshResponse {
  token: string
  refreshToken: string
  tokenExpirationDate: number
  refreshTokenExpirationDate: number
  // Re-issued (or omitted) per the experiment gate on the backend. We keep
  // the previous value if absent — only an explicit `null` removes it.
  ssoToken?: string | null
}

// --- Logout signal (mirrors iOS AuthService.logoutContinuation) ---

type SessionInvalidHandler = () => void
let onSessionInvalid: SessionInvalidHandler | null = null

export function setSessionInvalidHandler(handler: SessionInvalidHandler) {
  onSessionInvalid = handler
}

function invalidateSession() {
  clearSession()
  onSessionInvalid?.()
}

// --- Single-flight refresh (mirrors iOS refreshSingleFlight) ---

let refreshPromise: Promise<Session> | null = null

async function refreshToken(session: Session): Promise<Session> {
  // Check refresh token expiry (mirrors iOS refreshDeltaToExpiry >= 0)
  const refreshExpiresAt =
    session.refreshExpiresAt ?? Date.now() / 1000 + 20 * 24 * 60 * 60 // default: 20 days (iOS fallback)
  if (Date.now() / 1000 >= refreshExpiresAt) {
    invalidateSession()
    throw new Error('Refresh token expired')
  }

  const response = await fetch(`${env.apiBaseUrl}${API_PATHS.auth.refresh}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-app-platform': 'web' },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  })

  // Any refresh error → auto-logout (mirrors iOS clearLocal in catch)
  if (!response.ok) {
    invalidateSession()
    throw new Error('Token refresh failed')
  }

  const data = (await response.json()) as RefreshResponse

  const newSession: Session = {
    accessToken: data.token,
    refreshToken: data.refreshToken,
    expiresAt: data.tokenExpirationDate,
    refreshExpiresAt: data.refreshTokenExpirationDate,
    ssoToken:
      data.ssoToken === undefined ? (session.ssoToken ?? null) : data.ssoToken,
  }

  saveSession(newSession)
  return newSession
}

/**
 * Single-flight: only one refresh runs at a time, others wait.
 * Mirrors iOS refreshSingleFlight / GraphClient.RefreshCoordinator.
 */
function singleFlightRefresh(session: Session): Promise<Session> {
  if (!refreshPromise) {
    refreshPromise = refreshToken(session).finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/**
 * Force refresh — called on 401 retry.
 * Mirrors iOS AuthService.refresh().
 */
export async function forceRefresh(): Promise<string | null> {
  const session = loadSession()
  if (!session) return null

  try {
    const newSession = await singleFlightRefresh(session)
    return newSession.accessToken
  } catch {
    return null
  }
}

/**
 * Pre-request: ensure token is valid, refresh if expiring soon.
 * Mirrors iOS HeaderBuilder → authService.refreshIfNeeded().
 * Threshold: 300 seconds (5 minutes), same as iOS.
 */
export async function ensureValidToken(): Promise<string | null> {
  const session = loadSession()
  if (!session) return null

  if (!isSessionExpiringSoon(session)) {
    return session.accessToken
  }

  try {
    const newSession = await singleFlightRefresh(session)
    return newSession.accessToken
  } catch {
    return null
  }
}

export function getAccessToken(): string | null {
  const session = loadSession()
  return session?.accessToken ?? null
}
